import React from 'react'
import {TrashIcon} from '../../components/icons'
import {BoxProps, Button} from '../../vanilla'
import {MealDealGroup, PartialProductItem} from '../../types/store/basket'
import {useBasketStore, useCustomerStore, useProductStore} from '../../store/hooks/useStore'
import {flowResult} from 'mobx'
import {useRemoveFromCartTracking} from '../../analytics/click-event-tracking'
import {usePromotion} from '../../store/hooks/usePromotion'
import {redirectIfNotRegistered} from '../../utils/login-redirect-utils'
import {useLocation} from 'react-router-dom'
import useNavigation from '../../hooks/use-navigation'

export interface MealDealDeleteProps extends Omit<BoxProps<'div'>, 'onChange' | 'value'> {
  cartLocation?: string
  inCart?: boolean
  group: MealDealGroup | null
}

const MealDealRemove = ({group, cartLocation}: MealDealDeleteProps) => {
  const basketStore = useBasketStore()
  const sendRemoveFromCartData = useRemoveFromCartTracking()
  const promotion = usePromotion(group?.promoID)
  const {productsById} = useProductStore()

  const navigate = useNavigation()
  const {pathname} = useLocation()
  const {isRegistered, loading: loadingCustomer} = useCustomerStore()

  const removeMealDeal = async () => {
    if (!group) return

    const quantitiesToRemove = group.items.reduce<Record<string, number>>((quantities, item) => {
      quantities[item.productID] =
        (quantities[item.productID] || 0) + item.displayQuantity * group.quantity
      return quantities
    }, {})

    const productsToRemove: PartialProductItem[] = Object.entries(quantitiesToRemove).map(
      ([productId, quantityToRemove]) => {
        const currentQuantity = (basketStore.basket?.productItems || [])
          .filter((item) => item.productId === productId)
          .reduce((total, item) => total + (item.quantity || 0), 0)

        return {
          product: {id: productId} as PartialProductItem['product'],
          quantity: Math.max(0, currentQuantity - quantityToRemove)
        }
      }
    )

    /**
     * Prepare cart event data for the removed items with correct quantities
     * @returns Array of products with updated quantities for the cart event data
     */
    const removedItemCartEventData = () => {
      return (
        group?.items
          .map((item) => {
            const product = productsById[item.productID]

            if (product) {
              product.quantity = quantitiesToRemove[item.productID]
            }

            return product || null
          })
          .filter((product) => product != null) || []
      )
    }

    try {
      const response = await flowResult(
        basketStore.updateBasketItemQuantity(productsToRemove, {
          openMiniBasket: false,
          processAsGroup: true
        })
      )

      if (response?.success !== true || response?.failed?.length) {
        return
      }

      // Only prepare and send remove from cart data if the removal was successful
      const promotionPriceNumber = Number(promotion?.price)
      const cartEventData = {
        product: removedItemCartEventData(),
        cartLocation,
        value: !isNaN(promotionPriceNumber)
          ? (promotionPriceNumber * group.quantity).toFixed(2)
          : undefined,
        mealDealId: promotion?.id
      }
      sendRemoveFromCartData(cartEventData)
    } catch (error) {
      console.error('Failed to remove meal deal', error)
    }
  }

  return (
    <Button
      variant="unstyled"
      paddingX="0px"
      iconLeft={<TrashIcon color="gray800" style={{height: 22, width: 20}} />}
      style={{height: 'auto'}}
      data-test-selector="remove-icon"
      onClick={() => {
        if (!redirectIfNotRegistered({navigate, pathname, isRegistered, loadingCustomer})) {
          removeMealDeal()
        }
      }}
    />
  )
}

export default MealDealRemove
